import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Network } from 'lucide-react';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Spinner,
} from '@/components/ui';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHive, useHiveGraph } from '@/features/agents/hives/hooks/useHives';
import {
  useCreateHandoff,
  useHandoffs,
  useUpdateHandoff,
} from '@/features/agents/hives/hooks/useHandoffs';
import type {
  DelegationMode,
  HandoffCreate,
  HandoffSettings,
  HandoffType,
  HandoffUpdate,
  GraphNode,
} from '@/features/agents/hives/types';

// =============================================================================
// Handoff Create / Edit Page (Sprint 250 H5 — DEBT-13)
// =============================================================================

export function HandoffCreatePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { hiveId, handoffId } = useParams<{ hiveId: string; handoffId?: string }>();
  const isEdit = Boolean(handoffId);

  const { data: hive, isLoading: hiveLoading } = useHive(hiveId);
  const { data: graphData, isLoading: graphLoading } = useHiveGraph(hiveId);
  const { data: handoffsData } = useHandoffs(hiveId ?? '');
  const createMutation = useCreateHandoff(hiveId ?? '');
  const updateMutation = useUpdateHandoff(hiveId ?? '', handoffId ?? '');

  const [sourceAgentId, setSourceAgentId] = useState('');
  const [targetAgentId, setTargetAgentId] = useState('');
  const [type, setType] = useState<HandoffType>('delegate');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(0);
  // HandoffSettings (Sprint 250 DEBT-13) — applied at runtime.
  const [mode, setMode] = useState<DelegationMode | ''>('');
  const [timeoutSeconds, setTimeoutSeconds] = useState<number | ''>('');
  const [includeHistory, setIncludeHistory] = useState(false);
  const [maxTokens, setMaxTokens] = useState<number | ''>('');

  const graph = graphData as { nodes?: GraphNode[] } | undefined;
  const hiveAgents = useMemo(() => graph?.nodes ?? [], [graph]);

  // Preload in edit mode
  useEffect(() => {
    if (!isEdit || !handoffsData) return;
    const handoff = handoffsData.data?.find((h) => h.id === handoffId);
    if (!handoff) return;
    setSourceAgentId(handoff.source_agent_id);
    setTargetAgentId(handoff.target_agent_id);
    setType(handoff.type);
    setDescription(handoff.description ?? '');
    setPriority(handoff.priority ?? 0);
    const settings = handoff.settings ?? {};
    setMode((settings.mode as DelegationMode | undefined) ?? '');
    setTimeoutSeconds(settings.timeout_seconds ?? '');
    setIncludeHistory(Boolean(settings.include_history));
    setMaxTokens(settings.max_tokens ?? '');
  }, [isEdit, handoffId, handoffsData]);

  const canSubmit =
    sourceAgentId.length > 0 &&
    targetAgentId.length > 0 &&
    sourceAgentId !== targetAgentId;

  const buildSettings = (): HandoffSettings => {
    const settings: HandoffSettings = {};
    if (type === 'delegate' && mode) settings.mode = mode;
    if (typeof timeoutSeconds === 'number' && timeoutSeconds > 0) {
      settings.timeout_seconds = timeoutSeconds;
    }
    if (includeHistory) settings.include_history = true;
    if (typeof maxTokens === 'number' && maxTokens > 0) settings.max_tokens = maxTokens;
    return settings;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      const settings = buildSettings();
      if (isEdit) {
        const data: HandoffUpdate = {
          type,
          description: description.trim() || undefined,
          priority,
          settings,
        };
        await updateMutation.mutateAsync(data);
        toast.success(t('admin.hives.handoffUpdated') || 'Handoff updated');
      } else {
        const data: HandoffCreate = {
          source_agent_id: sourceAgentId,
          target_agent_id: targetAgentId,
          type,
          description: description.trim() || undefined,
          priority,
          settings,
        };
        await createMutation.mutateAsync(data);
        toast.success(t('admin.hives.handoffCreated') || 'Handoff created');
      }
      navigate(`/agents/hives/${hiveId}`);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      const fallback = isEdit
        ? t('admin.hives.handoffUpdateError') || 'Failed to update handoff'
        : t('admin.hives.handoffCreateError') || 'Failed to create handoff';
      toast.error(detail || fallback);
    }
  };

  if (hiveLoading || graphLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!hive) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">Hive not found</p>
        </div>
      </div>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-6" data-testid="handoff-create-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/agents/hives/${hiveId}`)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
            <Network className="h-6 w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {isEdit ? t('admin.hives.editHandoff') : t('admin.hives.newHandoff')}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {hive.name} &mdash;{' '}
              {t('admin.hives.handoffCreateSubtitle') || 'Configure a handoff between agents'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card className="p-6 space-y-6 max-w-2xl">
          {/* Source Agent (create only) */}
          <div>
            <Label htmlFor="source-agent">{t('admin.hives.handoffForm.sourceAgent')}</Label>
            <Select
              value={sourceAgentId}
              onValueChange={setSourceAgentId}
              disabled={isEdit}
            >
              <SelectTrigger id="source-agent" className="mt-1">
                <SelectValue placeholder={t('admin.hives.handoffForm.selectAgent')} />
              </SelectTrigger>
              <SelectContent>
                {hiveAgents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Direction indicator */}
          <div className="flex justify-center">
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </div>

          {/* Target Agent (create only) */}
          <div>
            <Label htmlFor="target-agent">{t('admin.hives.handoffForm.targetAgent')}</Label>
            <Select
              value={targetAgentId}
              onValueChange={setTargetAgentId}
              disabled={isEdit}
            >
              <SelectTrigger id="target-agent" className="mt-1">
                <SelectValue placeholder={t('admin.hives.handoffForm.selectAgent')} />
              </SelectTrigger>
              <SelectContent>
                {hiveAgents
                  .filter((a) => a.id !== sourceAgentId)
                  .map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {sourceAgentId && targetAgentId && sourceAgentId === targetAgentId && (
              <p className="text-sm text-red-500 mt-1">
                {t('admin.hives.handoffForm.sameAgentError')}
              </p>
            )}
          </div>

          {/* Type */}
          <div>
            <Label htmlFor="handoff-type">{t('admin.hives.handoffForm.type')}</Label>
            <Select value={type} onValueChange={(v) => setType(v as HandoffType)}>
              <SelectTrigger id="handoff-type" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="delegate">
                  {t('admin.hives.handoffForm.typeDelegate')}
                </SelectItem>
                <SelectItem value="transfer">
                  {t('admin.hives.handoffForm.typeTransfer')}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {type === 'delegate'
                ? t('admin.hives.handoffForm.delegateHelp')
                : t('admin.hives.handoffForm.transferHelp')}
            </p>
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="handoff-description">{t('admin.hives.handoffForm.description')}</Label>
            <Textarea
              id="handoff-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('admin.hives.handoffForm.descriptionPlaceholder')}
              className="mt-1"
              rows={3}
            />
          </div>

          {/* Priority */}
          <div>
            <Label htmlFor="handoff-priority">{t('admin.hives.handoffForm.priority')}</Label>
            <Input
              id="handoff-priority"
              type="number"
              value={priority}
              onChange={(e) => setPriority(Math.max(0, Math.min(100, Number(e.target.value))))}
              min={0}
              max={100}
              className="mt-1 w-32"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('admin.hives.handoffForm.priorityHelp')}
            </p>
          </div>

          {/* Settings (Sprint 250 DEBT-13) */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('admin.hives.handoffForm.settings')}
            </h3>

            {type === 'delegate' && (
              <div>
                <Label htmlFor="handoff-mode">{t('admin.hives.handoffForm.mode')}</Label>
                <Select
                  value={mode || 'default'}
                  onValueChange={(v) => setMode(v === 'default' ? '' : (v as DelegationMode))}
                >
                  <SelectTrigger id="handoff-mode" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">
                      {t('admin.hives.handoffForm.modeDefault')}
                    </SelectItem>
                    <SelectItem value="sync">{t('admin.hives.handoffForm.modeSync')}</SelectItem>
                    <SelectItem value="background">
                      {t('admin.hives.handoffForm.modeBackground')}
                    </SelectItem>
                    <SelectItem value="auto">{t('admin.hives.handoffForm.modeAuto')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="handoff-timeout">{t('admin.hives.handoffForm.timeout')}</Label>
                <Input
                  id="handoff-timeout"
                  type="number"
                  value={timeoutSeconds}
                  onChange={(e) =>
                    setTimeoutSeconds(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  min={1}
                  max={300}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="handoff-max-tokens">{t('admin.hives.handoffForm.maxTokens')}</Label>
                <Input
                  id="handoff-max-tokens"
                  type="number"
                  value={maxTokens}
                  onChange={(e) =>
                    setMaxTokens(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  min={256}
                  max={16384}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="handoff-include-history"
                checked={includeHistory}
                onChange={(e) => setIncludeHistory(e.target.checked)}
                className="h-4 w-4 text-purple-600 rounded"
              />
              <Label htmlFor="handoff-include-history">
                {t('admin.hives.handoffForm.includeHistory')}
              </Label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" type="button" onClick={() => navigate(`/agents/hives/${hiveId}`)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit || isPending}>
              {isPending ? (
                <Spinner size="sm" />
              ) : isEdit ? (
                t('common.save')
              ) : (
                t('common.create')
              )}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
